import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Box,
  Container,
  Paper,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import default_theme from "assets/themes/themes";

import { fetchLapsRequest } from "../../../redux/laps/lapSlice";
import { fastApiURL } from "../../../utils/config"; 

const CassandraLAPDetailPage = () => {
  const { lapId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // 1. Pull ONLY the LAP list from Redux
  const { list: lapsList, loading: lapsLoading } = useSelector((state) => state.laps);
  
  // 2. Local state for fetching public attached solutions
  const [attachedSolutions, setAttachedSolutions] = useState([]);
  const [isSolutionsLoading, setIsSolutionsLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchLapsRequest());
  }, [dispatch]);

  const rawLap = lapsList.find((l) => {
    const currentId = l.entryId || l.combinedData?.id || l.id;
    return String(currentId) === String(lapId);
  });
  
  const lap = rawLap ? (rawLap.combinedData || rawLap) : null;

  // 3. Fetch solutions directly from the public catalog API
  useEffect(() => {
    const fetchPublicSolutions = async () => {
      if (!lap?.solution_ids || lap.solution_ids.length === 0) {
        setAttachedSolutions([]);
        return;
      }

      setIsSolutionsLoading(true);
      try {
        // NOTE: Ensure this matches your actual public catalog endpoint path
        const responses = await Promise.all(
          lap.solution_ids.map(id => 
            fetch(`${fastApiURL}/api/catalogs/solutions-catalog/${id}`) 
          )
        );

        const data = await Promise.all(
          responses.map(res => (res.ok ? res.json() : null))
        );

        const validSolutions = data
          .filter(Boolean)
          .map(item => item.data || item);

        setAttachedSolutions(validSolutions);
      } catch (error) {
        console.error("Error fetching attached solutions:", error);
      } finally {
        setIsSolutionsLoading(false);
      }
    };

    if (lap) {
      fetchPublicSolutions();
    }
  }, [lap?.solution_ids]);

  const handleBackClick = () => {
    if (location.state?.fromCatalogList) {
      navigate("/solutions/rural-development/lap-catalog", {
        state: {
          searchQuery: location.state.searchQuery,
          selectedFilters: location.state.selectedFilters,
          currentPage: location.state.currentPage,
        },
      });
    } else {
      navigate("/solutions/rural-development/lap-catalog");
    }
  };

  const handleSolutionClick = (id) => {
    navigate(`/solutions/rural-development/solutions-catalog/${id}`, {
        state: {
            backPath: `/solutions/rural-development/lap-details/${lapId}`,
            backLabel: "Back to Local Action Plan"
        }
    });
  };

  const getSafeText = (val) => {
    if (!val) return "";
    if (typeof val === 'string') return val;
    if (val.en) return val.en;
    if (val.english) return val.english;
    return String(val);
  };

  if (lapsLoading && !lap) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh", backgroundColor: "#F9F6ED" }}>
        <CircularProgress sx={{ color: "#13A89E" }} />
      </Box>
    );
  }

  if (!lap) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5">LAP not found</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ flex: 1, backgroundColor: default_theme.palette.primary.white, minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        
        {/* Header bar */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleBackClick} sx={{ color: "#13A89E", p: 0, mr: 1 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography 
            variant="body2" 
            sx={{ color: "#13A89E", textTransform: "uppercase", fontWeight: 600, cursor: "pointer", letterSpacing: 0.5 }}
            onClick={handleBackClick}
          >
            Back to Local Action Plans
          </Typography>
        </Box>

        {/* Top Box: Title & Description */}
        <Paper sx={{ p: { xs: 4, md: 6 }, borderRadius: 2, border: "none", mb: 6, boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: "#333", mb: 3 }}>
            {lap.title || "Untitled LAP"}
          </Typography>
          
          <Typography variant="body1" sx={{ color: "#666", lineHeight: 1.8, fontSize: "1.05rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {lap.description || "No description provided."}
          </Typography>
        </Paper>

        {/* Bottom Section: Solutions Header */}
        {(lap.solution_ids?.length > 0) && (
          <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#333", mb: 1 }}>
                Local Action Plan catalogs
              </Typography>
              <Typography variant="body1" sx={{ color: "#666" }}>
                Select one of the catalogs below to view its full solution details.
              </Typography>
          </Box>
        )}

        {/* Solutions Grid */}
        {isSolutionsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#13A89E" }} />
          </Box>
        ) : (
          <Grid container spacing={4}>
          {attachedSolutions.map((solution) => {
              const data = solution.combinedData || solution;
              const id = solution.id || solution.entryId;
              
              const title = getSafeText(data.title_en || data.title_english || data.title) || "Untitled Solution";
              const description = getSafeText(data.story || data.aims || data.aim) || "No description available.";

              return (
              <Grid item xs={12} sm={6} md={4} key={id}>
                  <Paper 
                  onClick={() => handleSolutionClick(id)}
                  sx={{ 
                      p: 4, height: "100%", display: "flex", flexDirection: "column", 
                      borderRadius: 3, position: "relative", cursor: "pointer",
                      border: "none", boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }
                  }}
                  >
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: "#333", display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
                      {title}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ color: "#777", flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 7, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.7 }}>
                      {description}
                  </Typography>
                  </Paper>
              </Grid>
              );
          })}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default CassandraLAPDetailPage;